import { RecipeVariants } from '@vanilla-extract/recipes';
import { Typography } from '../typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClose,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import {
  alertRecipe,
  closeIcon,
  closeIconWrapper,
  closeWithLabel,
  icon,
  startAlignment,
  wrapper,
} from './alert.css';
import { clsx } from 'clsx';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReactNode, useState } from 'react';
import { isEmpty } from 'lodash';
import { Link } from 'react-router';
import { ExternalLink } from '../link/externalLink';

export type AlertVariant = NonNullable<
  NonNullable<RecipeVariants<typeof alertRecipe>>['variant']
>;

const AlertIcon: Record<AlertVariant, IconProp> = {
  info: faInfoCircle,
  warning: faExclamationTriangle,
  error: faExclamationCircle,
  success: faCheckCircle,
  neutral: faPlus,
};

type Action = {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
};

export type AlertProps = {
  title?: string;
  details?: string | string[];
  variant: AlertVariant;
  allowClose?: boolean;
  closeLabel?: string;
  alignStart?: boolean;
  action?: Action;
  wrapperClassName?: string;
  onClose?: () => void;
  customComponent?: ReactNode;
};

export const Alert = ({
  title,
  details,
  variant,
  allowClose,
  closeLabel,
  alignStart,
  action,
  wrapperClassName,
  onClose,
  customComponent,
}: AlertProps) => {
  const [open, setOpen] = useState(true);

  if (!open) return null;
  const detailsArray = !details
    ? []
    : Array.isArray(details)
    ? details
    : [details];

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <div
      className={clsx(
        wrapper,
        alignStart && startAlignment,
        alertRecipe({ variant }),
        wrapperClassName,
      )}
    >
      <FontAwesomeIcon
        icon={AlertIcon[variant]}
        className={icon}
        data-testid={`alert-icon-${variant}`}
      />
      <div className="flex flex-col gap-2">
        {title && (
          <Typography variant="body" size="large">
            {title}
          </Typography>
        )}
        {isEmpty(detailsArray)
          ? null
          : detailsArray.map((detail) => <Detail text={detail} key={detail} />)}
        {customComponent ? customComponent : null}
      </div>
      <div className="ml-auto">
        {!action ? null : <AlertAction {...action} />}
        {!allowClose ? null : closeLabel ? (
          <div
            className={closeWithLabel}
            onClick={close}
            data-testid="alertClose"
          >
            <Typography variant="body" size="medium">
              {closeLabel}
            </Typography>
            <FontAwesomeIcon icon={faClose} className={closeIcon} />
          </div>
        ) : (
          <div
            className={closeIconWrapper}
            onClick={close}
            data-testid="alertClose"
          >
            <FontAwesomeIcon icon={faClose} className={closeIcon} />
          </div>
        )}
      </div>
    </div>
  );
};

const Detail = ({ text }: { text: string }) => {
  const lines = text.includes('\n') ? text.split('\n') : [text];
  return (
    <div className="flex flex-col gap-1">
      {lines.map((line, index) => (
        <Typography
          key={`${index}-${line}`}
          variant="body"
          size="medium"
          fontWeight="normal"
        >
          {line}
        </Typography>
      ))}
    </div>
  );
};

const AlertAction = ({ label, to, href, onClick }: Action) => {
  if (to) {
    return (
      <Link to={to}>
        <Typography
          variant="body"
          size="medium"
          color="B-100"
          className="w-max pr-2"
          data-testid="alertAction"
        >
          {label}
        </Typography>
      </Link>
    );
  }

  if (href) {
    return <ExternalLink label={label} href={href} data-testid="alertAction" />;
  }

  if (onClick) {
    return (
      <Typography
        variant="body"
        size="medium"
        className="cursor-pointer pr-2"
        onClick={onClick}
        data-testid="alertAction"
      >
        {label}
      </Typography>
    );
  }
};
